import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { useStore } from "@/state/store";
import { AppFrame } from "@/components/AppFrame";
import { Eyebrow } from "@/components/primitives/Eyebrow";
import { Heading } from "@/components/primitives/Heading";
import { Hairline } from "@/components/primitives/Hairline";
import { Timer } from "@/components/primitives/Timer";
import { LiveDot } from "@/components/primitives/LiveDot";
import type { Team, TeamRequirement } from "@offsite/shared";

/**
 * Beacon Working-Stage (`/workshop/working`).
 * Brief 04 Screen 08 — VCLWorking.
 *
 * Hero-Timer (320px) zentriert, Eyebrow „WORKING · 90 MIN", links Team-Liste
 * mit Status-Dots, rechts „EINGEREICHT · n / m".
 */

interface TeamRow {
  team: Team;
  status: "draft" | "submitted" | "empty";
  submittedAt?: number;
}

export function WorkingStage() {
  const { state } = useStore();
  const token = state.auth.token;
  const phase = state.workshop.phase;
  const [rows, setRows] = useState<TeamRow[]>([]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const load = async () => {
      try {
        const teamsRes = await api.teams.list(token);
        if (cancelled) return;
        // Pro Team Requirement-Status holen — parallel.
        const reqs = await Promise.all(
          teamsRes.teams.map(async (t) => {
            try {
              const r = await api.teams.getReq(token, t.number);
              return r.requirement as TeamRequirement | null;
            } catch {
              return null;
            }
          }),
        );
        if (cancelled) return;
        const next: TeamRow[] = teamsRes.teams.map((team, i) => {
          const r = reqs[i];
          return {
            team,
            status: r?.status ?? "empty",
            submittedAt: r?.submittedAt,
          };
        });
        setRows(next);
      } catch {
        /* swallow */
      }
    };
    load();
    const id = setInterval(load, 4000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [token]);

  const total = rows.length;
  const submitted = rows.filter((r) => r.status === "submitted").length;

  return (
    <AppFrame skin="presenter" hideHeader hideFooter>
      <section
        style={{
          flex: 1,
          padding: "var(--s-7)",
          display: "grid",
          gridTemplateColumns: "1fr auto 1fr",
          gap: "var(--s-7)",
          alignItems: "center",
        }}
      >
        {/* Links: Teams-Liste */}
        <aside
          className="rise-1"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--s-4)",
            maxHeight: "70vh",
            overflow: "auto",
          }}
        >
          <Eyebrow>TEAMS</Eyebrow>
          <Hairline skin="presenter" />
          <ul
            style={{
              listStyle: "none",
              margin: 0,
              padding: 0,
              display: "flex",
              flexDirection: "column",
              gap: "var(--s-3)",
            }}
          >
            {rows.map((r) => (
              <li
                key={r.team.number}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "var(--s-3)",
                }}
              >
                <LiveDot
                  live={r.status === "draft"}
                  ok={r.status === "submitted"}
                />
                <span
                  className="mono"
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 13,
                    color: "var(--ink-300)",
                    textTransform: "uppercase",
                    letterSpacing: "var(--tracking-meta)",
                  }}
                >
                  T-{String(r.team.number).padStart(2, "0")}
                </span>
                <span
                  style={{
                    color:
                      r.status === "submitted"
                        ? "var(--svo-weiss, #fff)"
                        : "var(--ink-200)",
                    fontWeight: r.status === "submitted" ? 600 : 400,
                  }}
                >
                  {r.team.name ?? `Team ${r.team.number}`}
                </span>
              </li>
            ))}
          </ul>
        </aside>

        {/* Mitte: Hero-Timer */}
        <div
          className="rise-2"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "var(--s-5)",
            padding: "var(--s-7)",
          }}
        >
          <Eyebrow accent>WORKING · 90 MIN</Eyebrow>
          {phase.timerEndsAt ? (
            <Timer deadline={phase.timerEndsAt} size="hero" />
          ) : (
            <span
              className="mono"
              style={{
                fontFamily: "var(--font-mono)",
                fontVariantNumeric: "tabular-nums",
                fontSize: "clamp(160px, 18vw, 320px)",
                fontWeight: 500,
                color: "var(--ink-400)",
                letterSpacing: "var(--tracking-display)",
                lineHeight: 1,
              }}
            >
              00<span style={{ opacity: 0.4 }}>:</span>00
            </span>
          )}
          <Heading level="h2">90 Minuten.</Heading>
        </div>

        {/* Rechts: Submit-Counter */}
        <aside
          className="rise-3"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--s-4)",
            alignItems: "flex-end",
          }}
        >
          <Eyebrow accent>EINGEREICHT</Eyebrow>
          <span
            className="mono"
            style={{
              fontFamily: "var(--font-mono)",
              fontVariantNumeric: "tabular-nums",
              fontSize: "clamp(72px, 8vw, 128px)",
              fontWeight: 500,
              color: "var(--svo-weiss, #fff)",
              lineHeight: 1,
              whiteSpace: "nowrap",
            }}
          >
            {submitted} / {total}
          </span>
          <Hairline skin="presenter" />
          <span
            className="mono"
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 12,
              color: "var(--ink-400)",
              textTransform: "uppercase",
              letterSpacing: "var(--tracking-meta)",
            }}
          >
            {total === 0
              ? "WARTE AUF TEAMS"
              : submitted === total
                ? "ALLE TEAMS FERTIG"
                : `${total - submitted} OFFEN`}
          </span>
        </aside>
      </section>
    </AppFrame>
  );
}

